import { AlertTriangle, Inbox } from "lucide-react";
import type { ReactNode } from "react";
import { errorMessage } from "../../lib/format";
import { Button } from "./Button";
import { Spinner } from "./Spinner";

export function Card({ title, actions, children, className = "" }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`glass rounded-2xl ${className}`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-default px-5 py-3.5">
          {title && <h2 className="text-sm font-semibold tracking-tight text-default">{title}</h2>}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

const tones = {
  neutral: "bg-slate-500/10 text-slate-700 ring-1 ring-inset ring-slate-500/15",
  blue: "bg-accent-500/10 text-accent-700 ring-1 ring-inset ring-accent-500/20",
  green: "bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-500/20",
  amber: "bg-amber-500/10 text-amber-800 ring-1 ring-inset ring-amber-500/25",
  red: "bg-rose-500/10 text-rose-700 ring-1 ring-inset ring-rose-500/20",
} as const;
export type Tone = keyof typeof tones;

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-default">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted">
      <Spinner label={label} /> <span aria-hidden="true">{label}…</span>
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <Inbox className="h-8 w-8 text-subtle" aria-hidden="true" />
      <p className="text-sm font-medium text-default">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-2 py-8 text-center">
      <AlertTriangle className="h-7 w-7 text-amber-500" aria-hidden="true" />
      <p className="text-sm text-default">{errorMessage(error)}</p>
      {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return <p role="alert" className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 ring-1 ring-inset ring-rose-500/20">{errorMessage(error)}</p>;
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <nav aria-label="Pagination" className="mt-3 flex items-center justify-between text-sm text-muted">
      <span>
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>Previous</Button>
        <Button size="sm" onClick={() => onPage(page + 1)} disabled={page >= pages}>Next</Button>
      </div>
    </nav>
  );
}

export function Table({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="overflow-x-auto">
      <table aria-label={label} className="w-full min-w-[640px] border-collapse text-left text-sm">{children}</table>
    </div>
  );
}
export const Th = ({ children, className = "" }: { children?: ReactNode; className?: string }) => (
  <th scope="col" className={`border-b border-default px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-subtle ${className}`}>{children}</th>
);
export const Td = ({ children, className = "" }: { children?: ReactNode; className?: string }) => (
  <td className={`border-b border-default px-3 py-2.5 align-middle text-default ${className}`}>{children}</td>
);
